import React, { useState, useEffect, useRef, useCallback } from 'react';
import api, { backgroundApi } from '../../api/client';
import { MessageCircle, Send, FileUp, ChevronDown, ChevronUp, Smartphone } from 'lucide-react';

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return mins + 'm ago';
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return hrs + 'h ago';
  const days = Math.floor(hrs / 24);
  if (days < 7) return days + 'd ago';
  return new Date(dateStr).toLocaleDateString();
}

export default function PortalMessagesWidget({ recordId }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMsg, setNewMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [unread, setUnread] = useState(0);
  const [showDocRequest, setShowDocRequest] = useState(false);
  const [docTitle, setDocTitle] = useState('');
  const [docDesc, setDocDesc] = useState('');
  const [requestingDoc, setRequestingDoc] = useState(false);
  const [smsError, setSmsError] = useState('');
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  const loadMessages = useCallback(async () => {
    try {
      const res = await api.get('/portal-admin/messages/' + recordId);
      setMessages(res.data.messages || []);
    } catch (err) {
      console.error('Failed to load portal messages:', err);
    } finally {
      setLoading(false);
    }
  }, [recordId]);

  const loadStats = useCallback(async () => {
    try {
      const res = await api.get('/portal-admin/stats/' + recordId);
      setUnread(res.data.unread_messages || 0);
    } catch (err) {}
  }, [recordId]);

  useEffect(() => { loadMessages(); loadStats(); }, [loadMessages, loadStats]);
  // Auto-scroll disabled to prevent page jump

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const res = await backgroundApi.get('/portal-admin/messages/' + recordId);
        setMessages(res.data.messages || []);
      } catch (err) {}
      try {
        const res = await backgroundApi.get('/portal-admin/stats/' + recordId);
        setUnread(res.data.unread_messages || 0);
      } catch (err) {}
    }, 15000);
    return () => clearInterval(interval);
  }, [recordId]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMsg.trim() || sending) return;
    setSending(true);
    setSmsError('');
    try {
      await api.post('/portal-admin/messages/' + recordId, { body: newMsg.trim() });
      setNewMsg('');
      await loadMessages();
      inputRef.current?.focus({ preventScroll: true });
    } catch (err) {
      console.error('Failed to send:', err);
    } finally {
      setSending(false);
    }
  };

  const handleSendSMS = async () => {
    if (!newMsg.trim() || sending) return;
    setSending(true);
    setSmsError('');
    try {
      await api.post('/portal-admin/sms/' + recordId, { body: newMsg.trim() });
      setNewMsg('');
      await loadMessages();
      inputRef.current?.focus({ preventScroll: true });
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to send SMS';
      setSmsError(msg);
      console.error('SMS send failed:', err);
    } finally {
      setSending(false);
    }
  };

  const handleDocRequest = async (e) => {
    e.preventDefault();
    if (!docTitle.trim() || requestingDoc) return;
    setRequestingDoc(true);
    try {
      await api.post('/portal-admin/document-requests/' + recordId, {
        title: docTitle.trim(),
        description: docDesc.trim() || null,
      });
      setDocTitle('');
      setDocDesc('');
      setShowDocRequest(false);
      await loadMessages();
    } catch (err) {
      console.error('Failed to request doc:', err);
    } finally {
      setRequestingDoc(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Header */}
      <button onClick={() => setCollapsed(!collapsed)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors">
        <div className="flex items-center gap-2">
          <MessageCircle className="h-4 w-4 text-blue-600" />
          <span className="text-sm font-semibold text-gray-900">Client Portal Messages</span>
          {unread > 0 && (
            <span className="bg-red-500 text-white text-[10px] font-bold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1.5">
              {unread}
            </span>
          )}
        </div>
        {collapsed ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronUp className="h-4 w-4 text-gray-400" />}
      </button>

      {!collapsed && (
        <>
          {/* Messages */}
          <div className="border-t border-gray-100 max-h-80 overflow-y-auto px-4 py-3 space-y-2.5">
            {loading ? (
              <p className="text-xs text-gray-400 text-center py-4">Loading...</p>
            ) : messages.length === 0 ? (
              <div className="text-center py-6">
                <MessageCircle className="h-8 w-8 text-gray-200 mx-auto mb-2" />
                <p className="text-xs text-gray-400">No portal messages yet.</p>
                <p className="text-[10px] text-gray-300 mt-1">Messages from the client portal will appear here.</p>
              </div>
            ) : (
              messages.map(msg => {
                const isClient = msg.sender_type === 'client';
                const isSystem = msg.sender_type === 'system';
                if (isSystem) {
                  return (
                    <div key={msg.id} className="flex justify-center">
                      <span className="bg-gray-50 text-gray-500 text-[10px] px-2.5 py-1 rounded-full">{msg.body}</span>
                    </div>
                  );
                }
                return (
                  <div key={msg.id} className={`flex ${isClient ? 'justify-start' : 'justify-end'}`}>
                    <div className={`max-w-[85%] rounded-xl px-3 py-2 ${
                      isClient ? 'bg-blue-50 border border-blue-100' : 'bg-gray-100'
                    }`}>
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="text-[10px] font-semibold text-gray-500">
                          {isClient ? '👤 Client' : (msg.sender_first || 'Staff') + ' ' + (msg.sender_last || '')}
                        </span>
                        {msg.channel === 'sms' && <span className="text-[9px] bg-green-100 text-green-700 px-1.5 rounded font-medium">SMS</span>}
                      </div>
                      <p className="text-sm text-gray-900 whitespace-pre-wrap">{msg.body}</p>
                      <p className="text-[10px] text-gray-400 mt-1">{timeAgo(msg.created_at)}</p>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={bottomRef} />
          </div>

          {/* Actions */}
          <div className="border-t border-gray-100 p-3 space-y-2">
            {smsError && (
              <div className="bg-red-50 border border-red-100 rounded-lg px-3 py-2 text-xs text-red-700">
                {smsError}
                <button onClick={() => setSmsError('')} className="ml-2 text-red-400 hover:text-red-600">✕</button>
              </div>
            )}
            {/* Send Message */}
            <form onSubmit={handleSend} className="space-y-2">
              <input ref={inputRef} type="text" value={newMsg} onChange={e => setNewMsg(e.target.value)}
                placeholder="Reply to client..." maxLength={5000}
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              <div className="flex gap-2">
                <button type="submit" disabled={!newMsg.trim() || sending}
                  className="flex-1 bg-blue-600 text-white rounded-lg px-3 py-2 hover:bg-blue-700 disabled:opacity-40 transition-all flex items-center justify-center gap-1.5 text-xs font-semibold"
                  title="Send to Portal">
                  <Send className="h-3.5 w-3.5" /> Send to Portal
                </button>
                <button type="button" onClick={handleSendSMS} disabled={!newMsg.trim() || sending}
                  className="flex-1 bg-green-600 text-white rounded-lg px-3 py-2 hover:bg-green-700 disabled:opacity-40 transition-all flex items-center justify-center gap-1.5 text-xs font-semibold"
                  title="Send as SMS">
                  <Smartphone className="h-3.5 w-3.5" /> Send SMS
                </button>
              </div>
            </form>

            {/* Request Document */}
            {!showDocRequest ? (
              <button onClick={() => setShowDocRequest(true)}
                className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-blue-600 transition-colors">
                <FileUp className="h-3.5 w-3.5" />
                Request Document from Client
              </button>
            ) : (
              <form onSubmit={handleDocRequest} className="bg-amber-50 rounded-lg p-3 space-y-2 border border-amber-100">
                <p className="text-xs font-semibold text-amber-800">📋 Request a Document</p>
                <input type="text" value={docTitle} onChange={e => setDocTitle(e.target.value)}
                  placeholder="e.g., Insurance Card, Medical Records"
                  className="w-full px-3 py-1.5 bg-white border border-amber-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
                <input type="text" value={docDesc} onChange={e => setDocDesc(e.target.value)}
                  placeholder="Description (optional)"
                  className="w-full px-3 py-1.5 bg-white border border-amber-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
                <div className="flex gap-2">
                  <button type="submit" disabled={!docTitle.trim() || requestingDoc}
                    className="bg-amber-500 text-white text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-amber-600 disabled:opacity-50">
                    {requestingDoc ? 'Sending...' : 'Send Request'}
                  </button>
                  <button type="button" onClick={() => { setShowDocRequest(false); setDocTitle(''); setDocDesc(''); }}
                    className="text-xs text-gray-500 hover:text-gray-700">Cancel</button>
                </div>
              </form>
            )}
          </div>
        </>
      )}
    </div>
  );
}
